import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import TenantUsersTab from '@/pages/admin/tenants/components/TenantUsersTab';

interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'users', label: 'Users' },
];

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [data, setData] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (id) loadTenant();
  }, [id]);

  const loadTenant = async () => {
    setLoading(true);
    try {
      const result = await api.get<TenantDetail>(`/tenants/${id}`);
      setData(result);
    } catch (err) {
      addToast(getApiErrorMessage(err, 'Failed to load tenant'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      inactive: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
      suspended: { bg: 'rgba(209,34,41,0.1)', color: '#D12229' },
      pending: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
    };
    const style = map[status] ?? map.inactive;
    return (
      <span
        className="text-xs font-medium px-2.5 py-1 rounded-full capitalize"
        style={{ background: style.bg, color: style.color }}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout breadcrumb={['Administration', 'Tenants', 'Loading...']}>
        <div className="p-12 text-center">
          <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: '#6B6B70' }}></i>
          <p className="text-sm mt-3" style={{ color: '#6B6B70' }}>Loading tenant...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout breadcrumb={['Administration', 'Tenants', 'Not Found']}>
        <div className="p-12 text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#F5F5F7' }}>
            <i className="ri-error-warning-line text-xl" style={{ color: '#D12229' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>Tenant not found</p>
          <button
            onClick={() => navigate('/admin/tenants')}
            className="mt-4 px-4 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap"
            style={{ background: '#D12229', color: '#fff' }}
          >
            Back to Tenants
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout breadcrumb={['Administration', 'Tenants', data.name]}>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 transition-colors"
          >
            <i className="ri-arrow-left-line" style={{ color: '#6B6B70' }}></i>
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
            {data.name}
          </h1>
          {getStatusBadge(data.status)}
        </div>
        <div className="flex items-center gap-4 text-sm ml-11" style={{ color: '#6B6B70' }}>
          <span>
            Slug: <strong className="font-mono" style={{ color: '#1A1A1A' }}>{data.slug}</strong>
          </span>
          <span>·</span>
          <span>
            ID: <strong className="font-mono" style={{ color: '#1A1A1A' }}>{data.id}</strong>
          </span>
          <span>·</span>
          <span>Created {new Date(data.createdAt).toLocaleDateString('uk-UA')}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 mb-6 overflow-x-auto" style={{ borderBottom: '1px solid #E5E5E8' }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="px-4 py-3 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap relative"
            style={{
              color: activeTab === tab.key ? '#D12229' : '#6B6B70',
              borderBottom: activeTab === tab.key ? '2px solid #D12229' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E5E5E8' }}>
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 rounded-lg" style={{ background: '#F5F5F7' }}>
              <div className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: '#6B6B70' }}>
                Tenant Info
              </div>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B6B70' }}>Name</span>
                  <span className="font-medium" style={{ color: '#1A1A1A' }}>{data.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B6B70' }}>Slug</span>
                  <span className="font-mono font-medium" style={{ color: '#1A1A1A' }}>{data.slug}</span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span style={{ color: '#6B6B70' }}>Status</span>
                  {getStatusBadge(data.status)}
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B6B70' }}>Created</span>
                  <span className="font-medium" style={{ color: '#1A1A1A' }}>
                    {new Date(data.createdAt).toLocaleDateString('uk-UA')}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg" style={{ background: '#F5F5F7' }}>
              <div className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: '#6B6B70' }}>
                Identifiers
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-xs mb-1" style={{ color: '#6B6B70' }}>Tenant ID</div>
                  <div
                    className="font-mono text-xs px-3 py-2 rounded-md break-all"
                    style={{ background: '#fff', border: '1px solid #E5E5E8', color: '#1A1A1A' }}
                  >
                    {data.id}
                  </div>
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#6B6B70' }}>Slug</div>
                  <div
                    className="font-mono text-xs px-3 py-2 rounded-md"
                    style={{ background: '#fff', border: '1px solid #E5E5E8', color: '#1A1A1A' }}
                  >
                    {data.slug}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && id && <TenantUsersTab tenantId={id} />}
      </div>
    </DashboardLayout>
  );
}
